import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TemplateDesignElement } from '../../design-element';
import {
  ChannelUIds,
  CommunicationTemplate,
  CommunicationTemplateViewControl,
  MessageSettings
} from 'src/app/modules/communications/store/communications';
import produce from 'immer';
import { FormFieldElementSettings } from './form-field-control-model';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { DateFieldFormat, FieldDataType, NumberFormat, SchemaField, TextFieldFormat } from 'src/app/modules/core/store/models';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { Document } from 'src/app/modules/core/store/models';
import { FieldToValueMapping } from '../../design-elements.models';

@Component({
    selector: 'topo-template-form-field-element',
    templateUrl: './form-field-control.component.html',
    styleUrls: ['./form-field-control.component.scss'],
    standalone: false
})
export class TemplateDesignFormFieldElement implements OnInit, OnDestroy, TemplateDesignElement {
  @Input() index: number;
  @Input() messageSettings?: MessageSettings;
  @Input() template?: CommunicationTemplate;
  @Input() control?: CommunicationTemplateViewControl;
  @Input() set culture(value: string) {
    this._culture = value;
    if (!this.editMode && !!value) {
      this.render();
    }
  }
  @Input() orgLanguages: string[];
  @Input() selected: boolean;
  _culture: string;
  @Input() editable?: boolean;
  @Output() settingsChanged = new EventEmitter<{ uId: string; settings: any }>();
  @Output() hideControl = new EventEmitter();
  @Output() showControl = new EventEmitter();

  editMode: boolean;
  hideFromPreview: boolean;
  selectedDatasetUId: string;
  selectedDataset: Dataset;
  datasets: Dataset[];
  fields: SchemaField[];
  selectedFieldName: string;
  selectedSystemName: string;
  format: string;
  fieldDisplayName: string;
  fieldSystemName: string;
  fieldDataType: FieldDataType;
  fieldValue: string;
  TextFieldFormat = TextFieldFormat;
  private dataset: Dataset;
  private document: Document;
  private fieldMapping: FieldToValueMapping;
  tableWidth: string = "800px";

  constructor(
    private translateService: TranslateService,
    private templateDesignerService: TemplateDesignerService,
    private cultureService: CultureService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  loadSettings(): void {
    this.editMode = true;
    const settings = this.control?.settings as FormFieldElementSettings;

    this.datasets = [
      {
        name: this.translateService.instant('COMMUNICATIONS.SELECT-DATASET'),
        uId: null
      } as Dataset
    ];

    this.datasets.push(...this.templateDesignerService.datasets);
    this.selectedDatasetUId = !!settings?.datasetUId ? settings?.datasetUId : null;
    this.selectedDataset = this.datasets?.find(d => d.uId === this.selectedDatasetUId);

    if (!!this.selectedDataset) {
      this.datasetChanged(false);
    }
    this.selectedSystemName = settings?.systemName;
    this.selectedFieldName = this.templateDesignerService.getTranslateText(settings?.displayName, this._culture);
    this.format = settings?.format;
    this.fieldDataType = settings?.dataType;
    this.tableWidth = this.template?.channelUId === ChannelUIds.SlackChannelUId ? "500px" : "800px";
  }

  saveSettings(): boolean {
    return true;
  }

  render(): void {
    this.editMode = false;
    this.fieldDisplayName = this.translateService.instant('COMMUNICATIONS.FIELD-NAME');

    const settings = this.control?.settings as FormFieldElementSettings;
    if (!!settings) {
      this.selectedDatasetUId = !!settings?.datasetUId ? settings?.datasetUId : null;
      this.selectedDataset = this.templateDesignerService.datasets?.find(d => d.uId === this.selectedDatasetUId);
      if (!!this.selectedDataset) {
        const field = this.selectedDataset.schema?.fields.find(f => f.systemName === settings.systemName);
        if (!!field) {
          settings.format = field.settings?.format;
          settings.dataType = field.dataType;
        }
      }
      this.fieldDisplayName = this.templateDesignerService.getTranslateText(settings.displayName, this._culture);
      this.fieldValue = '';
      this.format = settings?.format;
      this.fieldDataType = settings?.dataType;

      if (this.editable === true) {
        this.fieldValue = settings.systemName ? `{{${settings.systemName}}}` : '';
        this.hideFromPreview = false;
        this.showControl?.emit();
        return;
      }

      if (!!this.document && !!this.fieldMapping) {
        if (!!this.fieldMapping[settings.systemName]) {
          this.fieldValue = this.fieldMapping[settings.systemName].value;
          if (!!!this.fieldValue) {
            this.fieldValue = '';
          }
          this.format = this.fieldMapping[settings.systemName].format;
        }
        else {
          this.fieldValue = '';
        }
        if (!!!this.fieldValue || this.fieldValue.toString().trim().length == 0) {
          this.hideFromPreview = true;
          this.hideControl?.emit();
        }
        else {
          this.hideFromPreview = false;
          this.showControl?.emit();
        }
      }
      else {
        this.hideFromPreview = true;
        this.hideControl?.emit();
      }
    }
    this.tableWidth = this.template?.channelUId === ChannelUIds.SlackChannelUId ? "500px" : "800px";
  }

  datasetChanged(fromUI: boolean): void {
    const selectedSystemName = this.selectedSystemName;
    this.selectedDataset = this.datasets?.find(d => d.uId === this.selectedDatasetUId);

    this.fields = [
      {
        displayName: this.translateService.instant('COMMUNICATIONS.SELECT-FIELD'),
        systemName: null
      } as SchemaField
    ];

    if (!this.selectedDataset?.schema?.fields) {
      return;
    }

    const fields = this.selectedDataset?.schema?.fields?.sort((a, b) =>
      this.getFieldDisplayName(a)?.toLocaleUpperCase() > this.getFieldDisplayName(b)?.toLocaleUpperCase() ? 1 : -1
    );

    this.fields.push(...fields);

    this.selectedSystemName = null;

    if (fromUI === true) {
      this.fieldChanged();
    }
  }

  fieldChanged(): void {
    const field = this.selectedDataset?.schema?.fields.find(f => f.systemName === this.selectedSystemName);
    const settings = {
      displayName: field?.displayNameByCulture,
      datasetUId: this.selectedDataset?.uId,
      systemName: this.selectedSystemName,
      format: field?.settings?.format,
      dataType: field?.dataType
    } as FormFieldElementSettings;

    this.selectedFieldName = this.templateDesignerService.getTranslateText(field?.displayNameByCulture, this._culture);
    this.format = field?.settings?.format;
    this.settingsChanged.emit({ uId: this.control.uId, settings: settings });
  }

  nameChanged(value: string): void {
    this.selectedFieldName = value;
    const settings = {
      displayName: {},
      datasetUId: this.selectedDataset?.uId,
      systemName: this.selectedSystemName,
      format: this.format,
      dataType: this.fieldDataType
    } as FormFieldElementSettings;

    this.selectedFieldName = value;

    settings.displayName[this._culture] = value;
    this.settingsChanged.emit({ uId: this.control.uId, settings: settings });
  }

  getFieldDisplayName(field: SchemaField): string {
    if (!!!field.displayNameByCulture) {
      return field.displayName;
    }
    let displayName = this.templateDesignerService.getTranslateText(field.displayNameByCulture, this._culture);
    return displayName;
  }

  setDocumentContext(dataset: Dataset, document: Document, mapUId: string, mapping: FieldToValueMapping) {
    this.dataset = dataset;
    this.document = document;
    this.fieldMapping = mapping;

    if (!this.editMode) {
      this.render();
    }
  }

  edit(): void {}

  setRuntimeData(data: any): void {
    if (!this.editMode && !!data) {
      this.document = data as Document;
      this.render();
    }
  }
}
